import { Problem } from "../common/problem.js";
import { isoDateTime, z } from "../zod.js";
import { Locale, localeKeys } from "../locales.js";
import { Email, TenantSummary } from "./schemas.js";

// Module 16 — the PR #6 tenancy/administration surface (spec 16 §5 subset):
// org tree (facilities/departments/work centers), members + provisioning,
// invites, roles + the permission catalog, tenant picker, password change.

export const Uuid = z.guid();
export { Locale };
export const ScopeKind = z.enum(["group", "facility", "department"]);
export const Currency = z.string().regex(/^[A-Z]{3}$/, "ISO-4217 alpha-3");

/** Label object over the locale registry (the i18n rule; work_centers.name_i18n).
 * en, ru, zh and fr are required, as they were before D-430; a locale added
 * since is optional, so the change stays additive. */
export const NameI18n = z
  .object({
    ...localeKeys(z.string().min(1).optional()),
    en: z.string().min(1),
    ru: z.string().min(1),
    zh: z.string().min(1),
    fr: z.string().min(1),
  })
  .openapi("NameI18n");

/**
 * A stored work centre's name bundle (D-339). The direct create route takes
 * all four locales, and the onboarding plant-map commit writes only the
 * tenant's locale when its draft carries a bare name (spec 02), so a stored
 * row can hold any non-empty subset. Readers fall back across locales.
 */
export const WorkCenterNameI18n = z
  .object(localeKeys(z.string().min(1).optional()))
  .openapi("WorkCenterNameI18n");

// --- org: facilities ---

export const FacilityKind = z.enum(["production", "warehouse", "office", "mixed"]);
export const ActiveStatus = z.enum(["active", "archived"]);

export const Facility = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    kind: FacilityKind,
    timezone: z.string(),
    currency: Currency,
    address: z.record(z.string(), z.unknown()).nullable(),
    status: ActiveStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("Facility");

export const FacilityCreateRequest = z
  .object({
    code: z.string().min(1).max(16),
    name: z.string().min(1).max(200),
    kind: FacilityKind.optional(),
    timezone: z.string().min(1).max(64).optional(),
    currency: Currency.optional(),
    address: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .openapi("FacilityCreateRequest");

export const FacilityPatchRequest = z
  .object({
    name: z.string().min(1).max(200).optional(),
    kind: FacilityKind.optional(),
    timezone: z.string().min(1).max(64).optional(),
    currency: Currency.optional(),
    address: z.record(z.string(), z.unknown()).nullable().optional(),
    status: ActiveStatus.optional(),
  })
  .openapi("FacilityPatchRequest");

export const FacilityListResponse = z
  .object({ items: z.array(Facility) })
  .openapi("FacilityListResponse");

// --- org: departments ---

export const Department = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    code: z.string(),
    name: z.string(),
    status: ActiveStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("Department");

export const DepartmentCreateRequest = z
  .object({
    facility_id: Uuid,
    code: z.string().min(1).max(16),
    name: z.string().min(1).max(200),
  })
  .openapi("DepartmentCreateRequest");

export const DepartmentPatchRequest = z
  .object({
    name: z.string().min(1).max(200).optional(),
    status: ActiveStatus.optional(),
  })
  .openapi("DepartmentPatchRequest");

export const DepartmentListResponse = z
  .object({ items: z.array(Department) })
  .openapi("DepartmentListResponse");

// --- org: work centers (spec 16 §2.19; rate fields are field-grant gated —
// serialized only to holders of catalogue.view_standard_costs /
// production.view_costs, hence optional in the response schema) ---

export const CapacityModel = z.enum(["machine_hours", "labor_hours", "units_per_shift"]);
export const ShiftPatternRef = z.enum(["one_shift", "two_shift", "three_shift"]);

export const WorkCenter = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    department_id: Uuid.nullable(),
    code: z.string(),
    name_i18n: WorkCenterNameI18n,
    work_center_role: z.string().nullable(),
    capacity_model: CapacityModel,
    shift_pattern_ref: ShiftPatternRef,
    labor_rate_minor_per_hr: z.number().int().optional(),
    overhead_rate_minor_per_hr: z.number().int().optional(),
    currency: Currency,
    status: ActiveStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("WorkCenter");

export const WorkCenterCreateRequest = z
  .object({
    facility_id: Uuid,
    department_id: Uuid.nullable().optional(),
    code: z.string().min(1).max(32),
    name_i18n: NameI18n,
    work_center_role: z.string().min(1).max(64).nullable().optional(),
    capacity_model: CapacityModel.optional(),
    shift_pattern_ref: ShiftPatternRef.optional(),
    labor_rate_minor_per_hr: z.number().int().min(0).optional(),
    overhead_rate_minor_per_hr: z.number().int().min(0).optional(),
  })
  .openapi("WorkCenterCreateRequest");

export const WorkCenterPatchRequest = z
  .object({
    name_i18n: NameI18n.optional(),
    work_center_role: z.string().min(1).max(64).nullable().optional(),
    capacity_model: CapacityModel.optional(),
    shift_pattern_ref: ShiftPatternRef.optional(),
    labor_rate_minor_per_hr: z.number().int().min(0).optional(),
    overhead_rate_minor_per_hr: z.number().int().min(0).optional(),
    status: ActiveStatus.optional(),
  })
  .openapi("WorkCenterPatchRequest");

export const WorkCenterListResponse = z
  .object({ items: z.array(WorkCenter) })
  .openapi("WorkCenterListResponse");

// --- members ---

export const MembershipView = z
  .object({
    id: Uuid,
    role_id: Uuid,
    role_code: z.string(),
    scope_kind: ScopeKind,
    facility_id: Uuid.nullable(),
    department_id: Uuid.nullable(),
    status: z.enum(["active", "revoked"]),
    created_at: isoDateTime(),
  })
  .openapi("MembershipView");

export const MemberListItem = z
  .object({
    user_id: Uuid,
    display_name: z.string(),
    email: z.string(),
    last_login_at: isoDateTime().nullable(),
    locked: z.boolean(),
    memberships: z.array(MembershipView),
  })
  .openapi("MemberListItem");

export const MemberListResponse = z
  .object({ items: z.array(MemberListItem) })
  .openapi("MemberListResponse");

export const MembershipCreateRequest = z
  .object({
    role_id: Uuid,
    scope_kind: ScopeKind,
    facility_id: Uuid.nullable().optional(),
    department_id: Uuid.nullable().optional(),
  })
  .openapi("MembershipCreateRequest");

export const MembershipPatchRequest = z
  .object({
    role_id: Uuid.optional(),
    scope_kind: ScopeKind.optional(),
    facility_id: Uuid.nullable().optional(),
    department_id: Uuid.nullable().optional(),
  })
  .openapi("MembershipPatchRequest");

export const MemberRevokeRequest = z
  .object({ reason: z.string().max(500).optional() })
  .openapi("MemberRevokeRequest");

export const ProvisionMemberRequest = z
  .object({
    email: Email,
    display_name: z.string().min(1).max(200).optional(),
    role_id: Uuid,
    scope_kind: ScopeKind,
    facility_id: Uuid.nullable().optional(),
    department_id: Uuid.nullable().optional(),
    temp_password: z.string().min(1).max(1024).optional(),
    reset: z.boolean().optional(),
  })
  .openapi("ProvisionMemberRequest");

export const ProvisionMemberResponse = z
  .object({
    user_id: Uuid,
    membership_id: Uuid.nullable(),
    // Returned exactly once (spec 16 §3.5a.2). The idempotency store keeps the
    // response without it, so a replay under the same key omits the key.
    temp_password: z.string().optional(),
    was_existing_user: z.boolean(),
  })
  .openapi("ProvisionMemberResponse");

export const RevokedCountResponse = z
  .object({ revoked_count: z.number().int().min(0) })
  .openapi("RevokedCountResponse");

// --- invites ---

export const Invite = z
  .object({
    id: Uuid,
    email: z.string(),
    role_id: Uuid,
    scope_kind: ScopeKind,
    facility_id: Uuid.nullable(),
    department_id: Uuid.nullable(),
    status: z.enum(["pending", "accepted", "expired", "revoked"]),
    locale: Locale,
    expires_at: isoDateTime(),
    resent_count: z.number().int().min(0).max(5),
    created_at: isoDateTime(),
  })
  .openapi("Invite");

export const InviteCreateRequest = z
  .object({
    email: Email,
    role_id: Uuid,
    scope_kind: ScopeKind,
    facility_id: Uuid.nullable().optional(),
    department_id: Uuid.nullable().optional(),
    message: z.string().max(1000).optional(),
    locale: Locale.optional(),
  })
  .openapi("InviteCreateRequest");

export const InviteListResponse = z
  .object({ items: z.array(Invite) })
  .openapi("InviteListResponse");

export const InviteLookupResponse = z
  .object({
    tenant_name: z.string(),
    role_name: z.string(),
    email: z.string(),
    expires_at: isoDateTime(),
    existing_user: z.boolean(),
  })
  .openapi("InviteLookupResponse");

export const InviteAcceptRequest = z
  .object({
    token: z.string().min(16).max(256),
    password: z.string().min(1).max(1024).optional(),
    display_name: z.string().min(1).max(200).optional(),
  })
  .openapi("InviteAcceptRequest");

export const InviteAcceptResponse = z
  .object({
    membership_id: Uuid,
    tenant_subdomain: z.string(),
    was_new_user: z.boolean(),
  })
  .openapi("InviteAcceptResponse");

// --- roles + catalog ---

export const RoleSummary = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    is_system: z.boolean(),
    member_count: z.number().int().min(0),
    permission_count: z.number().int().min(0),
  })
  .openapi("RoleSummary");

export const RoleListResponse = z
  .object({ items: z.array(RoleSummary) })
  .openapi("RoleListResponse");

export const RoleDetail = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    is_system: z.boolean(),
    cloned_from_role_id: Uuid.nullable(),
    permissions: z.array(z.string()),
  })
  .openapi("RoleDetail");

export const RoleCreateRequest = z
  .object({
    name: z.string().min(2).max(80),
    cloned_from_role_id: Uuid,
    description: z.string().max(500).optional(),
  })
  .openapi("RoleCreateRequest");

export const RolePatchRequest = z
  .object({
    name: z.string().min(2).max(80).optional(),
    description: z.string().max(500).nullable().optional(),
  })
  .openapi("RolePatchRequest");

export const RolePermissionsPatchRequest = z
  .object({
    add: z.array(z.string()).max(500).default([]),
    remove: z.array(z.string()).max(500).default([]),
  })
  .openapi("RolePermissionsPatchRequest");

const RoleReferenceCount = z.number().int().min(0);

/** The 409 body of `DELETE /v1/roles/{id}` (spec 16 §3.9.4, D-608). The
 * counts are present on `ROLE_IN_USE` and on no other code. */
export const RoleInUseProblem = Problem.extend({
  active_memberships: RoleReferenceCount.optional(),
  pending_invites: RoleReferenceCount.optional(),
  revoked_memberships: RoleReferenceCount.optional(),
  closed_invites: RoleReferenceCount.optional(),
  cloned_roles: RoleReferenceCount.optional(),
  kept_as_history: z.boolean().optional(),
}).openapi("RoleInUseProblem", {
  description:
    "A role delete refused. On `ROLE_IN_USE` it counts what names the role: `active_memberships`, `pending_invites`, `revoked_memberships`, `closed_invites` (accepted, expired or revoked) and `cloned_roles`. Only two have a clearing step: change the active members' role, and delete the clones that are not themselves kept. An invite in any status and a revoked membership are history that no request removes. `kept_as_history` is true when history on the role or on any role cloned from it means the delete can never succeed (spec 16 §3.9.4, D-608).",
});

export const PermissionItem = z
  .object({
    code: z.string(),
    namespace: z.string(),
    description: z.string(),
    is_field_grant: z.boolean(),
  })
  .openapi("PermissionItem");

export const PermissionListResponse = z
  .object({ items: z.array(PermissionItem) })
  .openapi("PermissionListResponse");

// --- auth additions (picker + password change) ---

/**
 * `GET /v1/auth/tenants`. The item is `TenantSummary` — the SAME schema the
 * login and TOTP-verify responses carry — rather than an inline copy of its
 * fields, which is what it was until 2026-09-11. One rule, one place: the
 * inline copy had already drifted once by omission (it never gained
 * `default_timezone`), and a second copy of a five-field object is a drift
 * waiting to happen, not a decoupling.
 */
export const TenantPickerResponse = z
  .object({ items: z.array(TenantSummary) })
  .openapi("TenantPickerResponse");

export const ChangePasswordRequest = z
  .object({
    current_password: z.string().min(1).max(1024),
    new_password: z.string().min(1).max(1024),
    totp_code: z
      .string()
      .regex(/^\d{6}$/)
      .optional(),
  })
  .openapi("ChangePasswordRequest");

/**
 * The session user's own profile (spec 16 §3.10, D-430). `locale` is the
 * user's saved product language; email, Telegram replies and documents
 * addressed to the user follow it, and the web reads it at sign-in.
 * `locale_saved` says whether the user ever saved a language through
 * `PATCH /v1/auth/profile`. A row's `locale` is otherwise a default (the
 * invite's language, or `en`), which email may use but which must not
 * override a language the user picked on a device. `tenant_default_locale` is
 * the default language of the tenant whose host served the request, null on a
 * host with no tenant. `impersonated` is true when a platform staff member is
 * using the session through an impersonation grant: the web then neither
 * applies the user's saved language nor saves a switcher choice to it, because
 * the language on screen is the staff member's and not the user's.
 */
/**
 * The impersonation banner's facts (spec 15 §4 S19, D-492): who from Assessly
 * is using this session, under which ticket, in which mode and until when.
 * Read from the session row module 16 wrote at redemption, never from schema
 * `platform`, which a tenant request cannot read. `staff_display_name` and
 * `ticket_ref` are null only on a session minted before migration 0116.
 */
export const ImpersonationBanner = z
  .object({
    grant_id: Uuid,
    staff_display_name: z.string().nullable(),
    ticket_ref: z.string().nullable(),
    mode: z.enum(["read_only", "act_as"]),
    expires_at: isoDateTime(),
  })
  .openapi("ImpersonationBanner");

export const AuthProfile = z
  .object({
    user_id: Uuid,
    email: z.string(),
    display_name: z.string(),
    locale: Locale,
    locale_saved: z.boolean(),
    phone_e164: z.string().nullable(),
    tenant_default_locale: Locale.nullable(),
    impersonated: z.boolean(),
    /** The member's tenant in an open grace suspension (spec 15 §3.2, D-627
     * ruling 3), for the shell's read-only banner; null otherwise. A hard
     * suspension answers 403 TENANT_SUSPENDED before this route runs. */
    tenant_suspension: z
      .object({
        mode: z.literal("grace"),
        grace_ends_at: isoDateTime(),
        reason_category: z.enum(["billing", "other"]),
        /** The tenant's own zone, the one the banner gives the date in. */
        timezone: z.string(),
      })
      .nullable(),
    /** Present exactly when `impersonated` is true (D-492). */
    impersonation: ImpersonationBanner.nullable(),
  })
  .openapi("AuthProfile");

const PhoneE164 = z.string().regex(/^\+[1-9][0-9]{5,14}$/, "E.164, e.g. +77010000000");

/** PATCH /v1/auth/profile (spec 16 §3.10). At least one field; `phone_e164: null` clears it. */
export const AuthProfilePatchRequest = z
  .object({
    display_name: z.string().trim().min(1).max(200).optional(),
    locale: Locale.optional(),
    phone_e164: PhoneE164.nullable().optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, {
    message: "name at least one of display_name, locale, phone_e164",
  })
  .openapi("AuthProfilePatchRequest");

/** Problem codes added by the PR #6 tenancy surface (UPPER_SNAKE, D-022). */
export const tenancyProblemCodes = [
  "TENANT_NOT_FOUND",
  "TENANT_PURGED",
  "TENANT_SUSPENDED",
  "TENANT_SUSPENDED_GRACE",
  "NOT_A_MEMBER",
  "PERMISSION_DENIED",
  "TOTP_ENROLLMENT_REQUIRED",
  "PASSWORD_CHANGE_REQUIRED",
  "IDEMPOTENCY_KEY_REQUIRED",
  "IDEMPOTENCY_KEY_REUSE",
  "IDEMPOTENCY_IN_PROGRESS",
  "SUBDOMAIN_INVALID",
  "SUBDOMAIN_RESERVED",
  "SUBDOMAIN_TAKEN",
  "INVALID_TENANT_STATUS",
  "TENANT_CURRENCY_LOCKED",
  "LAST_GROUP_ADMIN",
  "INVITE_PENDING",
  "INVITE_STALE",
  "INVITE_NOT_PENDING",
  "RESEND_LIMIT_REACHED",
  "ALREADY_MEMBER",
  "EXISTING_USER_LOGIN_REQUIRED",
  "PASSWORD_REQUIRED",
  "USER_EXISTS",
  "ROLE_NOT_PROVISIONABLE",
  "NOT_PROVISIONED_ACCOUNT",
  "PERMISSION_NOT_HELD",
  "SYSTEM_ROLE_IMMUTABLE",
  "ROLE_IN_USE",
  "ROLE_EXISTS",
  "WOULD_LOCK_OUT_ROLE_MANAGEMENT",
  "UNKNOWN_PERMISSION_CODE",
  "PLATFORM_ONLY_PERMISSION",
  "INVALID_SCOPE",
  "FACILITY_NOT_FOUND",
  "DEPARTMENT_NOT_FOUND",
  "ROLE_NOT_FOUND",
  "FACILITY_CODE_TAKEN",
  "DEPARTMENT_CODE_TAKEN",
  "WORK_CENTER_CODE_TAKEN",
  "FACILITY_IN_USE",
  "DEPARTMENT_IN_USE",
  "WORK_CENTER_IN_USE",
] as const;

export type TenancyProblemCode = (typeof tenancyProblemCodes)[number];
